import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Send, CheckCircle2, Loader2, Clock, ChevronRight, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * Pagina Contatti — modulo + storico richieste dell'utente autenticato.
 * - Modulo semplice (nome, email, oggetto, messaggio)
 * - POST /api/support/requests → persistenza + email di notifica se configurata
 * - GET /api/support/requests → storico filtrato lato server per user_id
 * - Click «Visualizza» → GET /api/support/requests/{id} → dettaglio modale
 *
 * Niente ticketing complesso, chat live o messaggistica in tempo reale.
 */
export default function Contact() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!user) return;
    setName([user.first_name, user.last_name].filter(Boolean).join(" ") || user.name || "");
    setEmail(user.email || "");
  }, [user]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/support/requests");
      setHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      // 401 means unauth; silently empty.
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (user) loadHistory();
  }, [user]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const messageValid = message.trim().length >= 10 && message.trim().length <= 4000;
  const subjectValid = subject.trim().length >= 1 && subject.trim().length <= 150;
  const nameValid = name.trim().length >= 1;
  const canSubmit = user && !submitting && nameValid && emailValid && subjectValid && messageValid;

  const submit = async (e) => {
    e?.preventDefault?.();
    setTouched({ name: true, email: true, subject: true, message: true });
    if (!canSubmit) {
      toast.error("Compila correttamente tutti i campi.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/support/requests", {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
      });
      setSent(true);
      setSubject("");
      setMessage("");
      toast.success("Richiesta inviata. Ti risponderemo via email appena possibile.");
      await loadHistory();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        const retry = Number(detail?.retry_after || 0);
        const mins = retry > 0 ? Math.ceil(retry / 60) : null;
        toast.error(
          mins
            ? `Hai raggiunto il limite di richieste. Riprova tra circa ${mins} minuti.`
            : "Hai raggiunto il limite di richieste. Riprova più tardi."
        );
      } else if (status === 401) {
        toast.error("Devi accedere per inviare una richiesta.");
      } else {
        toast.error("Impossibile inviare la richiesta. Riprova.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (requestId) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/support/requests/${requestId}`);
      setDetail(data);
    } catch {
      toast.error("Impossibile aprire la richiesta.");
    } finally {
      setDetailLoading(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) +
        " · " + d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-1)] text-[var(--fg-1)]" data-testid="contact-page">
      <Toaster richColors position="top-right" />
      <header className="sticky top-0 z-40 bg-[var(--glass-bg)] backdrop-blur-xl border-b border-[var(--border-c)]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to={user ? "/dashboard" : "/"} data-testid="contact-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--fg-3)] hover:text-[var(--fg-1)]" style={{ transition: "color 0.2s ease" }}>
            <ArrowLeft size={14} /> {user ? "Dashboard" : "Home"}
          </Link>
          <div className="font-serif text-2xl tracking-tight leading-none">
            Recen<span className="italic text-[#00F2F4]">Up</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-sans font-semibold text-[#00F2F4]">
            <Mail size={12} /> Contatti
          </div>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight">Scrivici</h1>
          <p className="mt-4 text-sm md:text-base text-[var(--fg-3)] max-w-2xl leading-relaxed">
            Hai bisogno di aiuto o vuoi segnalarci qualcosa? Compila il modulo. Rispondiamo di norma entro 1-2 giorni lavorativi all'indirizzo email che indichi qui sotto.
          </p>
        </div>

        {!user && (
          <div className="mb-8 rounded-xl border border-[var(--border-c)] bg-[var(--bg-2)] p-5" data-testid="contact-auth-cta">
            <p className="text-sm text-[var(--fg-2)]">
              Per inviare una richiesta devi avere un account.
              {" "}<Link to="/" className="text-[#00F2F4] hover:underline">Accedi o registrati</Link>.
            </p>
          </div>
        )}

        <section className="rounded-2xl border border-[var(--border-c)] bg-[var(--bg-2)] p-6 md:p-8">
          <h2 className="font-serif text-xl md:text-2xl tracking-tight">Nuova richiesta</h2>

          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nome" testId="contact-name-field" error={touched.name && !nameValid && "Inserisci il tuo nome."}>
                <input data-testid="contact-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, name: true }))} maxLength={100} className="w-full rounded-lg bg-[var(--bg-1)] border border-[var(--border-c)] px-4 py-3 text-sm font-sans text-[var(--fg-1)] focus:border-[#00F2F4] focus:outline-none" placeholder="Mario Rossi" disabled={!user || submitting} />
              </Field>
              <Field label="Email" testId="contact-email-field" error={touched.email && !emailValid && "Email non valida."}>
                <input data-testid="contact-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, email: true }))} className="w-full rounded-lg bg-[var(--bg-1)] border border-[var(--border-c)] px-4 py-3 text-sm font-sans text-[var(--fg-1)] focus:border-[#00F2F4] focus:outline-none" placeholder="tu@esempio.com" disabled={!user || submitting} />
              </Field>
            </div>

            <Field label="Oggetto" testId="contact-subject-field" error={touched.subject && !subjectValid && "L'oggetto è obbligatorio (max 150 caratteri)."}>
              <input data-testid="contact-subject-input" type="text" value={subject} onChange={(e) => setSubject(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, subject: true }))} maxLength={150} className="w-full rounded-lg bg-[var(--bg-1)] border border-[var(--border-c)] px-4 py-3 text-sm font-sans text-[var(--fg-1)] focus:border-[#00F2F4] focus:outline-none" placeholder="Problema con la card NFC" disabled={!user || submitting} />
            </Field>

            <Field label="Messaggio" testId="contact-message-field" hint={`${message.length}/4000`} error={touched.message && !messageValid && "Il messaggio deve avere almeno 10 caratteri."}>
              <textarea data-testid="contact-message-input" value={message} onChange={(e) => setMessage(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, message: true }))} maxLength={4000} rows={6} className="w-full rounded-lg bg-[var(--bg-1)] border border-[var(--border-c)] px-4 py-3 text-sm font-sans text-[var(--fg-1)] focus:border-[#00F2F4] focus:outline-none resize-y" placeholder="Descrivici il problema o la richiesta nel dettaglio…" disabled={!user || submitting} />
            </Field>

            <div className="flex items-center justify-end gap-3 pt-2">
              {sent && !submitting && (
                <span className="inline-flex items-center gap-1.5 text-xs text-[#3E7A5E] font-sans" data-testid="contact-sent-badge">
                  <CheckCircle2 size={14} /> Inviata
                </span>
              )}
              <button type="submit" disabled={!canSubmit} data-testid="contact-submit-btn" className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-6 py-3 text-sm font-sans font-semibold disabled:opacity-50 disabled:cursor-not-allowed" style={{ transition: "background-color 0.2s ease" }}>
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {submitting ? "Invio…" : "Invia richiesta"}
              </button>
            </div>
          </form>
        </section>

        {user && (
          <section className="mt-10 rounded-2xl border border-[var(--border-c)] bg-[var(--bg-2)] p-6 md:p-8" data-testid="contact-history-section">
            <h2 className="font-serif text-xl md:text-2xl tracking-tight">Le mie richieste</h2>
            <p className="mt-1 text-xs text-[var(--fg-3)]">Qui vedi lo storico delle richieste inviate dal tuo account.</p>

            <div className="mt-6">
              {historyLoading ? (
                <div className="text-sm text-[var(--fg-3)]">Caricamento…</div>
              ) : history.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[var(--border-c)] p-6 text-center text-sm text-[var(--fg-3)]" data-testid="contact-history-empty">
                  Non hai ancora inviato richieste.
                </div>
              ) : (
                <ul className="divide-y divide-[var(--border-c)]" data-testid="contact-history-list">
                  {history.map((r) => (
                    <li key={r.request_id} className="py-3 flex items-center gap-3" data-testid={`contact-history-row-${r.request_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs text-[var(--fg-3)] font-mono">
                          <Clock size={11} /> {fmtDate(r.created_at)}
                        </div>
                        <div className="mt-1 text-sm text-[var(--fg-1)] font-sans font-semibold truncate">{r.subject}</div>
                      </div>
                      <button onClick={() => openDetail(r.request_id)} data-testid={`contact-history-view-${r.request_id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-c)] hover:border-[#00F2F4] text-[var(--fg-1)] px-4 py-2 text-xs font-sans" style={{ transition: "border-color 0.2s ease" }}>
                        Visualizza <ChevronRight size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}
      </main>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6" onClick={() => setDetail(null)} data-testid="contact-detail-overlay">
          <div className="bg-[var(--bg-2)] border border-[var(--border-c)] rounded-t-2xl md:rounded-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="contact-detail-modal">
            <div className="sticky top-0 flex items-center justify-between gap-3 px-6 py-4 bg-[var(--bg-2)] border-b border-[var(--border-c)]">
              <h3 className="font-serif text-xl tracking-tight">Dettaglio richiesta</h3>
              <button onClick={() => setDetail(null)} data-testid="contact-detail-close" className="h-8 w-8 rounded-full hover:bg-[var(--bg-3)] flex items-center justify-center text-[var(--fg-2)]">
                <X size={16} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <Row label="Inviata" value={fmtDate(detail.created_at)} testId="contact-detail-date" />
              <Row label="Oggetto" value={detail.subject} testId="contact-detail-subject" />
              <Row label="Da" value={`${detail.name} · ${detail.email}`} testId="contact-detail-from" />
              <Row label="Stato" value={detail.email_sent ? "Inviata al team RecenUp" : "Ricevuta"} testId="contact-detail-status" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans mb-2">Messaggio</div>
                <div className="rounded-lg bg-[var(--bg-1)] border border-[var(--border-c)] p-4 text-sm text-[var(--fg-1)] whitespace-pre-wrap leading-relaxed" data-testid="contact-detail-message">
                  {detail.message}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {detailLoading && !detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Loader2 className="animate-spin text-white" size={28} />
        </div>
      )}

      <Footer />
    </div>
  );
}

function Field({ label, testId, error, hint, children }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between">
        <label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">{label}</label>
        {hint && <span className="text-[10px] text-[var(--fg-3)] font-mono">{hint}</span>}
      </div>
      <div className="mt-2">{children}</div>
      {error && <div className="mt-1.5 text-[11px] text-[#C44] font-sans">{error}</div>}
    </div>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex items-start gap-4" data-testid={testId}>
      <div className="w-24 flex-shrink-0 text-[10px] uppercase tracking-[0.15em] text-[var(--fg-3)] font-sans pt-0.5">{label}</div>
      <div className="flex-1 text-sm text-[var(--fg-1)] font-sans">{value}</div>
    </div>
  );
}
